import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, LayoutGrid, Globe, Bell, Plus, Minus } from "lucide-react";
import "./AppWebsiteTracking.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE = "Employee App and Website Monitoring Software | TeamGrid";
const DESCRIPTION =
  "Monitor app and website usage, categorize productivity and detect unusual patterns with smart alerts for complete digital visibility.";
const CANONICAL = "https://teamgrid.ai/features/app-website-tracking";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const TEAL = "#00D1C1";
const ICON_BG = "#E0FFFC";
const CARD_BORDER = "#EDF4FF";

const features = [
  {
    icon: LayoutGrid,
    title: "App Categories",
    sub: "Automatically categorize apps as productive, neutral, or distracting",
  },
  {
    icon: Globe,
    title: "Website Monitoring",
    sub: "Track browsing patterns and time spent on each domain",
  },
  {
    icon: Bell,
    title: "Smart Alerts",
    sub: "Get notified about unusual usage patterns or excessive distraction",
  },
];

const activities = [
  {
    name: "Google Chrome",
    abbr: "GC",
    time: "57h 39m",
    progress: 80,
    color: "#366EB6",
    iconBg: "#E0FFFC",
  },
  {
    name: "Cursor",
    abbr: "CR",
    time: "28h 23m",
    progress: 44,
    color: "#34A851",
    iconBg: "#BBF7D0",
  },
  {
    name: "Microsoft Teams",
    abbr: "MT",
    time: "5h 22m",
    progress: 22,
    color: "#6C59A6",
    iconBg: "#F7F7F7",
  },
];

const AppWebsiteTracking = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  const appWebsiteTrackingFAQs = [
    {
      question: "Does App & Website Tracking threaten privacy?",
      answer:
        "No, it tracks only active productivity signals from apps and URLs used during work, without capturing personal content, ensuring privacy remains intact.",
    },
  ];

  return (
    <div
      className="app-website-tracking-container"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient; content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 lg:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div
                className="max-w-7xl mx-auto flex flex-col items-center text-center"
                style={{ gap: 20 }}
              >
                <div
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1"
                  style={{
                    background: "rgba(0, 209, 193, 0.06)",
                    borderColor: "rgba(0, 209, 193, 0.15)",
                  }}
                >
                  <TrendingUp
                    className="h-3 w-3 flex-shrink-0"
                    style={{ color: TEAL }}
                  />
                  <span
                    className="font-bold uppercase tracking-wide"
                    style={{
                      fontSize: 12,
                      lineHeight: "16px",
                      letterSpacing: "0.6px",
                      color: TEAL,
                    }}
                  >
                    Activity Monitoring
                  </span>
                </div>
                <h1
                  className="font-extrabold text-black text-center"
                  style={{
                    fontSize: "clamp(2.5rem, 5vw, 62px)",
                    lineHeight: "clamp(50px, 8vw, 72px)",
                  }}
                >
                  App & Website <span style={{ color: TEAL }}>Tracking</span>
                </h1>
                <p
                  className="mx-auto max-w-[768px] text-center px-4 sm:px-0"
                  style={{
                    fontSize: "clamp(16px, 4vw, 20px)",
                    lineHeight: "clamp(24px, 6vw, 28px)",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  Monitor application and website usage patterns. Understand
                  exactly how your team spends their digital time.
                </p>
              </div>
            </div>

            {/* Section 2: Two columns - responsive padding, white bg, max-width 1280px, gap 64px */}
            <section
              className="flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8"
              style={{
                paddingTop: "clamp(40px, 8vw, 80px)",
                paddingBottom: "clamp(20px, 4vw, 40px)",
                paddingLeft: "clamp(20px, 4vw, 80px)",
                paddingRight: "clamp(20px, 4vw, 80px)",
                background: "#FFFFFF",
              }}
            >
              <div className="mb-6 flex w-full max-w-[1280px] mx-auto flex-col gap-16 lg:flex-row lg:items-center lg:justify-center lg:gap-16">
                {/* Left: Complete Digital Visibility */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-1 flex-col items-start lg:max-w-[608px]"
                  style={{ gap: 20 }}
                >
                  <h2
                    className="font-bold text-black"
                    style={{ fontSize: 36, lineHeight: "44px" }}
                  >
                    Complete Digital Visibility
                  </h2>
                  <p
                    style={{
                      fontSize: 17,
                      lineHeight: "24px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Track every application and website your team uses.
                    Categorize productive vs unproductive activities and set
                    smart alerts for unusual patterns.
                  </p>

                  <div
                    className="flex flex-col"
                    style={{ paddingTop: 24, gap: 32 }}
                  >
                    {features.map((f, i) => (
                      <div key={i} className="flex items-start gap-6">
                        <div
                          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                          style={{
                            background: ICON_BG,
                            boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                          }}
                        >
                          <f.icon className="h-5 w-5" style={{ color: TEAL }} />
                        </div>
                        <div className="flex flex-col" style={{ gap: 7 }}>
                          <h3
                            className="font-bold text-black"
                            style={{ fontSize: 15, lineHeight: "28px" }}
                          >
                            {f.title}
                          </h3>
                          <p
                            style={{
                              fontSize: 15,
                              lineHeight: "23px",
                              color: "rgba(0,0,0,0.6)",
                            }}
                          >
                            {f.sub}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ paddingTop: 24 }}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/book-demo")}
                      className="rounded-xl font-bold text-black"
                      style={{
                        padding: "12px 32px",
                        background: TEAL,
                        boxShadow: "0px 0px 15px rgba(0, 209, 193, 0.3)",
                        fontSize: 16,
                        lineHeight: "24px",
                      }}
                    >
                      View Activity
                    </motion.button>
                  </div>
                </motion.div>

                {/* Right: Activity Tracking card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex flex-1 flex-col lg:max-w-[608px]"
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${CARD_BORDER}`,
                    boxShadow: "0px 20px 40px -5px rgba(0, 0, 0, 0.1)",
                    borderRadius: 32,
                  }}
                >
                  <div className="flex flex-col gap-1 p-6">
                    <div className="flex items-center gap-2">
                      <TrendingUp
                        className="h-5 w-5 flex-shrink-0"
                        style={{ color: TEAL }}
                      />
                      <h3
                        className="font-bold text-black"
                        style={{ fontSize: 20, lineHeight: "28px" }}
                      >
                        Activity Tracking
                      </h3>
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        lineHeight: "16px",
                        fontWeight: 500,
                        color: "rgba(0,0,0,0.6)",
                      }}
                    >
                      Most used applications and websites.
                    </p>
                  </div>

                  <div className="flex flex-col gap-5 px-6 pb-6">
                    {activities.map((a, idx) => (
                      <div key={idx} className="flex flex-col gap-5">
                        <div className="flex items-center gap-4">
                          <div
                            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl"
                            style={{ background: a.iconBg }}
                          >
                            <span
                              className="font-bold text-center text-xs"
                              style={{ color: a.color, lineHeight: "16px" }}
                            >
                              {a.abbr}
                            </span>
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <span
                              className="font-bold text-black"
                              style={{ fontSize: 14, lineHeight: "20px" }}
                            >
                              {a.name}
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                lineHeight: "16px",
                                fontWeight: 500,
                                color: "#A0A0A0",
                              }}
                            >
                              {a.time}
                            </span>
                          </div>
                        </div>
                        <div
                          className="h-[11px] w-full overflow-hidden rounded-full"
                          style={{
                            background: "#F7F7F7",
                            boxShadow:
                              "inset 0px 3.45px 10.35px rgba(164, 164, 164, 0.3)",
                          }}
                        >
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${a.progress}%`,
                              background: a.color,
                              boxShadow:
                                "inset 3.45px 3.45px 10.35px rgba(255, 255, 255, 0.3)",
                            }}
                          />
                        </div>
                      </div>
                    ))}

                    <p
                      className="text-center font-bold uppercase tracking-wide"
                      style={{
                        fontSize: 15,
                        lineHeight: "16px",
                        letterSpacing: "0.55px",
                        color: "#A0A0A0",
                        paddingTop: 8,
                      }}
                    >
                      Showing 1 to 3 of 204 items
                    </p>
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Testimonials Section */}
            <Testimonials />

            {/* FAQ Section - Light Grey Background (full-bleed left/right) */}
            <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-16 px-4 sm:px-6 md:px-8 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about TeamGrid for app and
                    website tracking.
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {appWebsiteTrackingFAQs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppWebsiteTracking;
